import api from '@/helpers/api';
import {
	type PaymentRequestCreateBody,
	PaymentRequestCreatedOrigin,
	type PaymentRequestDB,
	type PaymentRequestUpdateBody,
	type PopulatedPaymentRequest,
	type StatusFilter,
} from '@/modules/paymentRequest/types';

import type { Pagination } from '@/types/common';

const getPaymentRequestByWorkerId = async (
	workerId: string
): Promise<{ items: PaymentRequestDB[] }> => {
	const response = await api.request<{ items: PaymentRequestDB[] }>({
		url: `/payment-requests/${workerId}`,
		method: 'GET',
	});
	return response.data;
};

const normalizePaymentRequest = (
	item: PopulatedPaymentRequest
): PopulatedPaymentRequest => ({
	...item,
	users: item.users.map((user) => ({
		...user,
		id: user.id ?? (user as unknown as { _id: string })._id,
	})),
});

const getPaymentRequestByAgencyId = async ({
	page,
	limit,
	search,
	status,
}: {
	page: number;
	limit: number;
	search: string;
	status: StatusFilter;
}): Promise<Pagination<PopulatedPaymentRequest>> => {
	const response = await api.request<Pagination<PopulatedPaymentRequest>>({
		url: `/payment-requests`,
		method: 'GET',
		params: {
			page,
			limit,
			search,
			status,
		},
	});

	return {
		...response.data,
		items: response.data.items.map(normalizePaymentRequest),
	};
};

const createPaymentRequest = async ({
	body,
}: {
	body: PaymentRequestCreateBody;
}): Promise<PaymentRequestDB> => {
	const payload = {
		workerId: body.workerId,
		origin: (body.origin ?? PaymentRequestCreatedOrigin.AGENCY).toUpperCase(),
		requestedAmount: body.requestedAmount,
	};
	const response = await api.request<PaymentRequestDB>({
		url: `/payment-requests`,
		method: 'POST',
		data: payload,
	});
	return response.data;
};

const updatePaymentRequest = async ({
	paymentRequestId,
	body,
}: {
	paymentRequestId: string;
	body: PaymentRequestUpdateBody;
}): Promise<PaymentRequestDB> => {
	const response = await api.request<PaymentRequestDB>({
		url: `/payment-requests/${paymentRequestId}`,
		method: 'PATCH',
		data: body,
	});
	return response.data;
};

const updateRequestedAmount = async ({
	paymentRequestId,
	requestedAmount,
}: {
	paymentRequestId: string;
	requestedAmount: number;
}): Promise<void> => {
	await api.request<void>({
		url: `/payment-requests/${paymentRequestId}/requested-amount`,
		method: 'PATCH',
		data: { requestedAmount },
	});
};

export default {
	getPaymentRequestByWorkerId,
	getPaymentRequestByAgencyId,
	createPaymentRequest,
	updatePaymentRequest,
	updateRequestedAmount,
};
